import { EXO_SKELETON_EVENT_NAMES } from './consts';
import {
  ExoSkeletonRuntimeDataEvent,
  createPostMessageEventHandler,
  hasExoSkeletonListenerInitialized,
  sendRuntimeDataEvent,
} from './runtime';

export const TRACKING_HOST_MESSAGE_EVENT_NAME =
  EXO_SKELETON_EVENT_NAMES.TRACKING_HOST_MESSAGE_EVENT_NAME;

export type PerformanceTrackingAbortReasons = 'timeout' | 'unmount';
export const TOTAL_REMOTE_LOADING_TIME = 'total-remote-loading-time';

export const TRACKING_ACTIONS = {
  ABORT: 'abort',
  START: 'start',
  END: 'end',
} as const;

type TrackingActions = typeof TRACKING_ACTIONS;
export type TrackingAction = TrackingActions[keyof TrackingActions];

export type TrackingMetadata = {
  displayName?: string;
  [key: string]: unknown;
};

export type TrackingData = {
  scope: string;
  moduleName: string;
  isRemoteAlreadyInitialized?: boolean;
  action: TrackingAction;
  trackingId: string;
  parentTrackingId?: string;
  metadata?: TrackingMetadata;
};

type RuntimeTrackingEvent = ExoSkeletonRuntimeDataEvent<
  typeof TRACKING_HOST_MESSAGE_EVENT_NAME,
  TrackingData
>;

export interface ICreateTrackingHostParameters {
  onTrack(event: RuntimeTrackingEvent): void;
}

function isTrackingDataEvent(data: unknown): data is RuntimeTrackingEvent {
  return (
    (data as RuntimeTrackingEvent)?.type === TRACKING_HOST_MESSAGE_EVENT_NAME
  );
}

export function createPerformanceTracker({
  onTrack,
}: ICreateTrackingHostParameters) {
  return createPostMessageEventHandler(
    TRACKING_HOST_MESSAGE_EVENT_NAME,
    function ({ data }: MessageEvent<unknown>) {
      if (isTrackingDataEvent(data)) {
        onTrack(data);
      }
    },
  );
}

export function abortTracking({
  scope,
  moduleName,
  trackingId,
  reason,
}: Pick<TrackingData, 'scope' | 'moduleName' | 'trackingId'> & {
  reason: PerformanceTrackingAbortReasons;
}) {
  if (!hasExoSkeletonListenerInitialized(TRACKING_HOST_MESSAGE_EVENT_NAME)) {
    console.warn(
      '"abortTracking" was called, but the ExoSkeleton host did not initialize the "createPerformanceTracker". Data sent:',
      { scope, moduleName, trackingId, reason },
    );
  }

  return sendRuntimeDataEvent<
    typeof TRACKING_HOST_MESSAGE_EVENT_NAME,
    TrackingData
  >(TRACKING_HOST_MESSAGE_EVENT_NAME, {
    action: TRACKING_ACTIONS.ABORT,
    scope,
    moduleName,
    trackingId,
    metadata: {
      reason,
    },
  });
}

export function trackLoadingTime(data: TrackingData) {
  if (!hasExoSkeletonListenerInitialized(TRACKING_HOST_MESSAGE_EVENT_NAME)) {
    console.warn(
      '"trackLoadingTime" was called, but the ExoSkeleton host did not initialize the "createPerformanceTracker". Data sent:',
      data,
    );
  }

  const {
    scope,
    action,
    trackingId,
    parentTrackingId,
    moduleName,
    isRemoteAlreadyInitialized,
    metadata,
  } = data;

  if (!scope) {
    throw new Error(
      'Cannot execute "trackLoadingTime" without scope, please provide your scope i.e.: trackLoadingTime({ scope: "accMyScope", moduleName: "./App" })',
    );
  }

  if (!moduleName) {
    throw new Error(
      'Cannot execute "trackLoadingTime" without moduleName, please provide your moduleName i.e.: trackLoadingTime({ scope: "accMyScope", moduleName: "./App" })',
    );
  }

  return sendRuntimeDataEvent<
    typeof TRACKING_HOST_MESSAGE_EVENT_NAME,
    TrackingData
  >(TRACKING_HOST_MESSAGE_EVENT_NAME, {
    action,
    scope,
    isRemoteAlreadyInitialized,
    moduleName,
    trackingId,
    parentTrackingId,
    metadata,
  });
}
